import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { http } from '@/lib/http';
import { http as atsHttp } from '@/lib/ats-https';

import bootstrapReducer from './bootstrap';
import circlesReducer from './circles';
import circleCandidateReducer from './circleCandidates';
import loginReducer from './auth';
import outreachReducer from './outreach';
import templatesReducer from './templates';
import stepReducer from './step';
import usersReducer from './users';
import jobsReducer from './ats';
import outreachesReducer from './outreaches';
import contactsReducer from './contacts';
import filtersReducer from './filters';
import flashReducer, { setFlash } from './flash';
import blueprintReducer from './blueprint';
import accountIntegrationsReducer from './accountIntegrations';
import accountInvitationsReducer from './accountInvitations';
import accountUsersReducer from './accountUsers';
import meReducer from './me';
import {
  apiQuery,
  notificationApiQuery,
  publicApiQuery,
  careerPortalApiQuery,
  atsApiQuery,
  agentforgeApiQuery,
} from './query';
import dashboardCirclesReducer from './dashboard/circles';
import dashboardSnapshotReducer from './dashboard/snapshot';
import dashboardOutreachReducer from './dashboard/outreach';
import mcoReducer from './mco';
import tasksReducer from './tasks';
import mcoTemplateReducer from './mco-templates';
import notificationReducer from './notification';
import workspaceUsersReducer from './workspaceUsers';
import flowHealthReportReducer from './reports/filter';
import candidateTouchpointReducer from './reports/flow-health/candidate-touchpoint';
import userActivityReducer from './reports/flow-health/user-activity';
import diversityReducer from './reports/diversity';
import flowKpiReducer from './reports/kpi/overall';
import teamProductivityReducer from './reports/team-productivity';
import atsReportReducer from './reports/ats';
import projectDetailsReducer from './projectDetails';
import emailTemplatesSettingsReducer from './emailTemplatesSettings';
import usersSettingsReducer from './usersSettings';
import interviewsListReducer from './interviewsList';
import contactsListReducer from './contactsList';
import jobsReferralsReducer from './jobsReferrals';
import internalJobsReducer from './internalJobs';
import internalApplicationsReducer from './internalApplications';
import referredCandidatesReducer from './referredCandidates';
import offerLetterTemplatesReducer from './offerLetterTemplates';
import applicationFormSettingsReducer from './applicationFormSettings';
import projectsReducer from './projects';
import reviewsReducer from './reviews';
import atsJobsReducer from './atsJobs';
import atsJobCreateReducer from './atsJobCreate';
import atsJobDetailsReducer from './atsJobDetails';
import pipelineReducer from './atsJobPipeline';
import atsCandidateRedcer from './atsCandidate';
import atsInterviewsReducer from './interviews';
import externalAccountInvitationsReducer from './externalAccountInvitations';
import externalAccountUsersReducer from './externalAccountUsers';
import careerPortalReducer from './career-portal';
import scoreCardReducer from './score-card';
import interviewPlanReducer from './interview-plan';
import schedulingReducer from './atsScheduling';
import interviewVideoReducer from './interview-video';
import hireReportsReducer from './atsReports/hire';
import interviewReportsReducer from './atsReports/interviews';
import rejectionsReportsReducer from './atsReports/rejections';
import offersReportsReducer from './atsReports/offers';
import jobReportsReducer from './atsReports/jobs';
import contactsReportsReducer from './atsReports/contacts';
import performanceReportsReducer from './atsReports/performance';
import quickAppReviewReducer from './atsQuickAppReview';
import atsDashboardReducer from './atsDashboard';
import pendingActivitiesReducer from './pending-activities';
import jobDescriptionTemplatesReducer from './jobDescriptionTemplates';
import atsApplicationReducer from './atsApplication';
import pipelineReportsReducer from './atsReports/pipeline';
import liveJobPipelineReportsReducer from './atsReports/liveJobPipeline';
import eeoReportsReducer from './atsReports/eeo';
import atsJobDetailsDashboardReducer from './atsJobDetailsDashboard';
import jobsTasksReducer from './jobTasks';
import customReportReducer from './custom-report';
import staticReportReducer from './static-report';
import integrationsReducer from './integrations';
import requisitionsReportsReducer from './atsReports/requisitions';
import surveyReportsReducer from './atsReports/survey';
import { queryClient } from '@/lib/queryClient';

const combinedReducer = combineReducers({
  bootstrap: bootstrapReducer,
  auth: loginReducer,
  circles: circlesReducer,
  scheduling: schedulingReducer,
  interviewVideo: interviewVideoReducer,
  circleCandidates: circleCandidateReducer,
  outreach: outreachReducer,
  templates: templatesReducer,
  step: stepReducer,
  users: usersReducer,
  outreaches: outreachesReducer,
  contacts: contactsReducer,
  filters: filtersReducer,
  flash: flashReducer,
  blueprint: blueprintReducer,
  jobs: jobsReducer,
  accountIntegrations: accountIntegrationsReducer,
  accountInvitations: accountInvitationsReducer,
  externalAccountInvitations: externalAccountInvitationsReducer,
  accountUsers: accountUsersReducer,
  externalAccountUsers: externalAccountUsersReducer,
  me: meReducer,
  mco: mcoReducer,
  dashboardCircles: dashboardCirclesReducer,
  dashboardSnapshot: dashboardSnapshotReducer,
  dashboardOutreach: dashboardOutreachReducer,
  tasks: tasksReducer,
  mcoTemplates: mcoTemplateReducer,
  notification: notificationReducer,
  workspaceUsers: workspaceUsersReducer,
  flowHealthReport: flowHealthReportReducer,
  candidateTouchpointReport: candidateTouchpointReducer,
  userActivityReport: userActivityReducer,
  diversityReport: diversityReducer,
  flowKpiReport: flowKpiReducer,
  teamProductivityReport: teamProductivityReducer,
  atsReport: atsReportReducer,
  projectDetails: projectDetailsReducer,
  emailTemplatesSettings: emailTemplatesSettingsReducer,
  usersSettings: usersSettingsReducer,
  interviewsList: interviewsListReducer,
  contactsList: contactsListReducer,
  jobsReferrals: jobsReferralsReducer,
  internalJobs: internalJobsReducer,
  internalApplications: internalApplicationsReducer,
  referredCandidates: referredCandidatesReducer,
  offerLetterTemplates: offerLetterTemplatesReducer,
  jobDescriptionTemplates: jobDescriptionTemplatesReducer,
  applicationFormSettings: applicationFormSettingsReducer,
  projects: projectsReducer,
  reviews: reviewsReducer,
  atsJobs: atsJobsReducer,
  atsJobCreate: atsJobCreateReducer,
  atsJobDetails: atsJobDetailsReducer,
  pipelineData: pipelineReducer,
  atsCandidate: atsCandidateRedcer,
  atsInterviews: atsInterviewsReducer,
  atsApplication: atsApplicationReducer,
  careerPortal: careerPortalReducer,
  scoreCard: scoreCardReducer,
  interviewPlan: interviewPlanReducer,
  hireReports: hireReportsReducer,
  offersReports: offersReportsReducer,
  interviewReports: interviewReportsReducer,
  rejectionsReports: rejectionsReportsReducer,
  pipelineReports: pipelineReportsReducer,
  liveJobPipelineReports: liveJobPipelineReportsReducer,
  eeoReports: eeoReportsReducer,
  jobsReports: jobReportsReducer,
  contactsReports: contactsReportsReducer,
  performanceReports: performanceReportsReducer,
  requisitionsReports: requisitionsReportsReducer,
  surveyReports: surveyReportsReducer,
  quickAppReviewData: quickAppReviewReducer,
  atsDashboardData: atsDashboardReducer,
  pendingActivitiesData: pendingActivitiesReducer,
  atsJobDetailsDashboardData: atsJobDetailsDashboardReducer,
  customReports: customReportReducer,
  staticReports: staticReportReducer,
  jobTasksData: jobsTasksReducer,
  integrations: integrationsReducer,
  [apiQuery.reducerPath]: apiQuery.reducer,
  [notificationApiQuery.reducerPath]: notificationApiQuery.reducer,
  [publicApiQuery.reducerPath]: publicApiQuery.reducer,
  [careerPortalApiQuery.reducerPath]: careerPortalApiQuery.reducer,
  [atsApiQuery.reducerPath]: atsApiQuery.reducer,
  [agentforgeApiQuery.reducerPath]: agentforgeApiQuery.reducer,
});

const rootReducer = (state, action) => {
  // Note - Clean all the data in the redux store, when logout;
  if (action.type === 'auth/logout') {
    state = {};
  }
  return combinedReducer(state, action);
};

function flashMiddleware(store) {
  return function wrapDispatch(next) {
    return function handleAction(action) {
      const { meta, payload } = action || {};
      if (meta?.requestStatus === 'fulfilled' && payload) {
        // only for async thunk actions that have been fulfilled
        const { flash } = payload;
        if (flash) {
          // dispatch `setFlash` asynchronously so it runs *after* the current action
          Promise.resolve().then(() => store.dispatch(setFlash(flash)));
        }
      }
      if (action?.type) {
        return next?.(action);
      }
    };
  };
}

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: {
        extraArgument: { http, atsHttp, queryClient },
      },
      serializableCheck: false,
    }).concat([
      flashMiddleware,
      apiQuery.middleware,
      notificationApiQuery.middleware,
      publicApiQuery.middleware,
      careerPortalApiQuery.middleware,
      atsApiQuery.middleware,
      agentforgeApiQuery.middleware,
    ]),
});
